<template>
    <c-modal
        :value="true"
        size="lg"
        :title="`Manage access to ${entityTypeNames[props.entityType].plural}`"
        @closed="forceCloseHandler"
    >
        <div class="row mb-3">
            <div class="d-flex w-100 mx-3 py-3 bg-light rounded">
                <div class="col-3">
                    <small class="text-muted">Name</small>
                    <p class="mb-0">{{ props.member?.user.Name }}</p>
                </div>
                <div class="col">
                    <small class="text-muted">E-mail</small>
                    <p class="mb-0">{{ props.member?.user.Email }}</p>
                </div>
            </div>
        </div>

        <div v-if="mode == 'list' && props.entityType == EntityType.TEMPLATE" class="row mb-3">
            <div class="col-12">
                <div class="d-flex align-items-center">
                    <label
                        class="mr-3 mb-0 text-capitalize"
                        v-text="entityTypeNames[props.entityType].plural + ' restriction access:'"
                    ></label>
                    <b-dropdown :text="getAccessModeText(accessRestriction)" variant="outline-primary">
                        <b-dropdown-item @click="updateAccessRestriction(AccessRestriction.LISTED, props.entityType)">
                            {{ getAccessModeText(AccessRestriction.LISTED) }}
                        </b-dropdown-item>
                        <b-dropdown-item
                            @click="updateAccessRestriction(AccessRestriction.FULL_ACCESS, props.entityType)"
                        >
                            {{ getAccessModeText(AccessRestriction.FULL_ACCESS) }}
                        </b-dropdown-item>
                        <b-dropdown-item
                            @click="
                                updateAccessRestriction(AccessRestriction.ALL_READ_ONLY_PLUS_LISTED, props.entityType)
                            "
                        >
                            {{ getAccessModeText(AccessRestriction.ALL_READ_ONLY_PLUS_LISTED) }}
                        </b-dropdown-item>
                    </b-dropdown>
                </div>
            </div>
        </div>

        <div v-if="showStoresDropdown" class="store-selector-wrapper mb-3">
            <b-dropdown ref="storesDropdown" split variant="primary" :text="selectedStore?.Url">
                <b-dropdown-item
                    v-for="store in availableStores"
                    :key="`store-${store.Id}`"
                    :value="store.Url"
                    :active="$store.state.store == store.Url"
                    @click="onStoresDropdownClick(store)"
                >
                    <div>{{ store.Url }}</div>
                </b-dropdown-item>
            </b-dropdown>
        </div>

        <div class="entityContentContainer">
            <div class="entityContent" :class="{ disabled: accessRestriction === AccessRestriction.FULL_ACCESS }">
                <!-- Modal search -->
                <div v-if="mode == 'add'" class="d-flex justify-content-between">
                    <h6
                        class="font-weight-bold align-content-center mb-0"
                        v-text="
                            'Select the ' + entityTypeNames[props.entityType].singular + ' you want to give access to'
                        "
                    />
                    <div class="d-flex justify-content-end">
                        <b-form-input
                            v-model="searchString"
                            class="ml-3 w-50 h-100 search-input-gray"
                            type="text"
                            debounce="400"
                            placeholder="Search"
                        />
                    </div>
                </div>

                <div v-if="mode == 'list'" class="d-flex justify-content-between">
                    <h6 class="font-weight-bold" v-text="'Allowed ' + entityTypeNames[props.entityType].plural" />
                    <div class="d-flex justify-content-end">
                        <button
                            class="btn btn-outline-primary"
                            @click="changeToAddMode()"
                            v-text="'Add  ' + entityTypeNames[props.entityType].singular"
                        />
                        <b-form-input
                            v-model="searchString"
                            class="ml-3 w-50 h-100 search-input-gray"
                            type="text"
                            debounce="400"
                            placeholder="Search"
                            style="width: unset"
                        />
                    </div>
                </div>
                <!-- Modal search -->

                <!-- No rows to display -->
                <div v-if="mode == 'add' ? false : !entitiesAdded.length" class="text-center text-muted py-4">
                    <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 30 30" fill="none">
                        <g clip-path="url(#clip0_182_7372)">
                            <path
                                d="M15 2.8125C18.2323 2.8125 21.3323 4.09654 23.6179 6.38214C25.9035 8.66774 27.1875 11.7677 27.1875 15C27.1875 18.2323 25.9035 21.3323 23.6179 23.6179C21.3323 25.9035 18.2323 27.1875 15 27.1875C11.7677 27.1875 8.66774 25.9035 6.38214 23.6179C4.09654 21.3323 2.8125 18.2323 2.8125 15C2.8125 11.7677 4.09654 8.66774 6.38214 6.38214C8.66774 4.09654 11.7677 2.8125 15 2.8125ZM15 30C18.9782 30 22.7936 28.4196 25.6066 25.6066C28.4196 22.7936 30 18.9782 30 15C30 11.0218 28.4196 7.20644 25.6066 4.3934C22.7936 1.58035 18.9782 0 15 0C11.0218 0 7.20644 1.58035 4.3934 4.3934C1.58035 7.20644 0 11.0218 0 15C0 18.9782 1.58035 22.7936 4.3934 25.6066C7.20644 28.4196 11.0218 30 15 30ZM12.6562 19.6875C11.877 19.6875 11.25 20.3145 11.25 21.0938C11.25 21.873 11.877 22.5 12.6562 22.5H17.3438C18.123 22.5 18.75 21.873 18.75 21.0938C18.75 20.3145 18.123 19.6875 17.3438 19.6875H16.875V14.5312C16.875 13.752 16.248 13.125 15.4688 13.125H12.6562C11.877 13.125 11.25 13.752 11.25 14.5312C11.25 15.3105 11.877 15.9375 12.6562 15.9375H14.0625V19.6875H12.6562ZM15 11.25C15.4973 11.25 15.9742 11.0525 16.3258 10.7008C16.6775 10.3492 16.875 9.87228 16.875 9.375C16.875 8.87772 16.6775 8.40081 16.3258 8.04918C15.9742 7.69754 15.4973 7.5 15 7.5C14.5027 7.5 14.0258 7.69754 13.6742 8.04918C13.3225 8.40081 13.125 8.87772 13.125 9.375C13.125 9.87228 13.3225 10.3492 13.6742 10.7008C14.0258 11.0525 14.5027 11.25 15 11.25Z"
                                fill="#0D0324"
                            />
                        </g>
                        <defs>
                            <clipPath id="clip0_182_7372">
                                <rect width="30" height="30" fill="white" />
                            </clipPath>
                        </defs>
                    </svg>
                    <p v-text="'There are no ' + entityTypeNames[props.entityType].plural + ' allowed yet'" />
                </div>
                <!-- No rows to display -->

                <!-- List Table to see already added entities -->
                <b-table
                    v-if="mode == 'list' && entitiesAdded && entitiesAdded.length > 0"
                    class="w-100 table"
                    striped
                    hover
                    thead-class="table-header"
                    :items="entitiesAdded"
                    :fields="getTableFields()"
                    responsive="sm"
                    :busy="loading"
                    :empty-text="'No ' + entityTypeNames[props.entityType].plural"
                    :empty-filtered-text="'No ' + entityTypeNames[props.entityType].plural + ' matching your search'"
                >
                    <template #table-busy>
                        <div class="align-items-center d-flex justify-content-center my-2 text-center my-2">
                            <b-spinner variant="primary" />
                            <strong class="ml-3 primary-text">Loading...</strong>
                        </div>
                    </template>

                    <template #cell(image)="data">
                        <c-preview
                            v-if="data.item.thumbnail"
                            :src="data.item.thumbnail"
                            thumbnail
                            hide-wrapper
                            class="img"
                        />
                        <font-awesome-icon v-else :icon="['fas', 'image']" size="10x" />
                    </template>

                    <template #cell(name)="data">
                        <div class="d-flex flex-column align-items-baseline">
                            <span>{{ data.item.name }}</span>
                            <span class="text-with-overflow link" style="color: var(--Primary-primary, #7258e0)">{{
                                data.item.id
                            }}</span>
                        </div>
                    </template>

                    <template #cell(actions)="data">
                        <div class="d-flex align-items-end">
                            <b-btn variant="outline-primary" :disabled="loading" @click="removeEntityAccess(data.item)">
                                Remove
                            </b-btn>
                        </div>
                    </template>
                </b-table>
                <!-- List Table to see already added entities -->

                <!-- List Table to see entities that can be added -->
                <div v-if="mode == 'add'">
                    <div class="w-100 d-flex align-items-center check-head-container">
                        <c-checkbox
                            :aria-labelledby="'checkbox-label'"
                            :value="generalCheck"
                            @input="checkAllPage($event)"
                        />
                        <span id="checkbox-label">{{
                            capitalizeFirstLetter(entityTypeNames[entityType].singular)
                        }}</span>
                        <b-btn
                            v-if="isSomethingChecked"
                            size="sm"
                            variant="outline-primary"
                            :disabled="loading"
                            class="mr-1 ml-3"
                            @click="changeCheckedEntityAccess('add')"
                        >
                            Select
                        </b-btn>
                        <b-btn
                            v-if="isSomethingChecked"
                            size="sm"
                            variant="outline-primary"
                            :disabled="loading"
                            @click="changeCheckedEntityAccess('remove')"
                        >
                            Remove
                        </b-btn>
                    </div>
                    <b-table
                        class="w-100 table"
                        striped
                        hover
                        thead-class="table-header"
                        :items="entitiesToDisplay"
                        :fields="getTableFields()"
                        responsive="sm"
                        :busy="loading"
                        :empty-text="'No ' + entityTypeNames[props.entityType].plural"
                        :empty-filtered-text="
                            'No ' + entityTypeNames[props.entityType].plural + ' matching your search'
                        "
                    >
                        <template #table-busy>
                            <div class="align-items-center d-flex justify-content-center my-2 text-center my-2">
                                <b-spinner variant="primary" />
                                <strong class="ml-3 primary-text">Loading...</strong>
                            </div>
                        </template>

                        <template #cell(isChecked)="data">
                            <div class="w-100 d-flex align-items-center">
                                <c-checkbox
                                    v-model="data.item.isChecked"
                                    label=""
                                    @input="storeCheck($event, data.item)"
                                />
                            </div>
                        </template>

                        <template #cell(image)="data">
                            <c-preview
                                v-if="data.item.thumbnail"
                                :src="data.item.thumbnail"
                                thumbnail
                                hide-wrapper
                                class="img"
                            />
                            <font-awesome-icon v-else :icon="['fas', 'image']" size="10x" />
                        </template>

                        <template #cell(name)="data">
                            <div class="d-flex flex-column align-items-baseline">
                                <span>{{ data.item.name }}</span>
                                <span class="text-with-overflow link" style="color: var(--Primary-primary, #7258e0)">{{
                                    data.item.id
                                }}</span>
                            </div>
                        </template>

                        <template #cell(actions)="data">
                            <div class="d-flex align-items-end">
                                <b-btn
                                    v-if="!entitiesAdded.some((t) => t.id == data.item.id)"
                                    variant="outline-primary"
                                    :disabled="loading"
                                    @click="addEntityAccess(data.item)"
                                >
                                    Select
                                </b-btn>
                                <b-btn
                                    v-if="entitiesAdded.some((t) => t.id == data.item.id)"
                                    variant="outline-primary"
                                    :disabled="loading"
                                    @click="removeEntityAccess(data.item)"
                                >
                                    Remove
                                </b-btn>
                            </div>
                        </template>
                    </b-table>
                </div>

                <!-- List Table to see entities that can be added -->

                <div class="d-flex justify-content-center">
                    <b-pagination
                        v-if="totalCount / itemsPerPage > 1"
                        v-model="currentPage"
                        size="md"
                        :disabled="loading"
                        :total-rows="totalCount"
                        :per-page="itemsPerPage"
                    />
                </div>
            </div>
            <div v-if="accessRestriction === AccessRestriction.FULL_ACCESS" class="overlay">
                <strong class="mr-2">Full Access:</strong>
                <span
                    v-text="
                        'This user can edit any ' +
                        entityTypeNames[props.entityType].singular +
                        ' and has no restrictions.'
                    "
                ></span>
            </div>
        </div>

        <template #modal-footer>
            <div class="d-flex justify-content-between w-100">
                <c-button text="Cancel" variant="secondary" @click="cancelHandler" />
                <c-button text="Confirm" variant="primary" @click="execLoadedFn(confirmHandler)" />
            </div>
        </template>
    </c-modal>
</template>

<script setup lang="ts">
import { computed, onMounted, Ref, ref, watch } from 'vue';
import { useLoader } from '@/composables/loader';
import CModal from '@/components/CModal.vue';
import CButton from '@/components/shared/CButton';
import CPreview from '@/components/shared/CPreview.vue';
import CCheckbox from '@/components/shared/CCheckbox.vue';
import {
    getTeamMemberTemplates,
    getTeamMemberLibraries,
    removeTeamMemberEntities,
    addTeamMemberEntities,
    getTeamMemberStores,
    getTeamMemberOptionSets,
    getTeamMemberProducts,
    updateTeamMemberAccessRestriction,
    getTeamMemberAccessRestriction
} from '@/api/team';
import { buildCloudFrontUrl } from '@/utils/pathUtils';
import { getTemplates } from '@/api/templateProduct';
import { EntityData, EntityType, TeamMember, AccessRestriction } from '@/api/typings/team';
import { getLibraries } from '@/api/libraries';
import { getStores } from '@/api/user';
import { getOptionSetsWithCount } from '@/api/storesApp/optionSets';
import { Store } from '@/typings/user';
import { getProductsWithCount } from '@/api/storesApp/products';
import { BDropdown } from 'bootstrap-vue/src/components/dropdown';

const emit = defineEmits<{
    (event: 'permissions-saved', value: TeamMember): void;
    (event: 'member-updated', value: TeamMember): void;
    (event: 'cancel'): void;
}>();

const props = defineProps<{
    member: TeamMember | undefined;
    entityType: EntityType;
}>();

const { execLoadedFn } = useLoader();

const entitiesAdded = ref<EntityData[]>([]);
const searchEntities = ref<EntityData[]>([]);
const entitiesToDisplay = ref<EntityData[]>([]);
const loading = ref(true);
const showStoresDropdown = ref(false);
const storesDropdown: Ref<BDropdown | null> = ref(null);
const availableStores = ref<Store[]>([]);
const selectedStore = ref<Store>();
const searchString = ref('');
const currentPage = ref(1);
const itemsPerPage = 10;
const totalCount = ref(0);
const mode = ref('list');
const checkedEntities = ref<EntityData[]>([]);
const generalCheck = computed(() => entitiesToDisplay.value.some((entity) => entity.isChecked));
const isSomethingChecked = computed(() => checkedEntities.value.some((entity) => entity.isChecked));

const accessRestriction = ref<AccessRestriction>(AccessRestriction.LISTED);

type EntityTypeNames = Record<EntityType, { singular: string; plural: string }>;

const entityTypeNames = ref<EntityTypeNames>({
    [EntityType.TEMPLATE]: { singular: 'template', plural: 'templates' },
    [EntityType.LIBRARY]: { singular: 'library', plural: 'libraries' },
    [EntityType.DESIGN]: { singular: 'design', plural: 'designs' },
    [EntityType.OPTION_SET]: { singular: 'option set', plural: 'option sets' },
    [EntityType.PRODUCT]: { singular: 'product', plural: 'products' },
    [EntityType.STORE]: { singular: 'store', plural: 'stores' },
    [EntityType.INTEGRATIONS]: { singular: 'integrations', plural: 'integrations' }
});

const capitalizeFirstLetter = (text: string) => {
    return text.charAt(0).toUpperCase() + text.slice(1);
};

const tableFieldsWithThumbnail = [
    { key: 'isChecked', label: '', thStyle: { width: '50px', padding: '0.5rem' }, tdClass: 'checkCell' },
    { key: 'image', label: '' },
    { key: 'name', label: '' },
    { key: 'actions', label: '' }
];

const tableFields = [
    { key: 'isChecked', label: '', thStyle: { width: '50px', padding: '0.5rem' }, tdClass: 'checkCell' },
    { key: 'name', label: '' },
    { key: 'actions', label: '' }
];

const getTableFields = () => {
    if (props.entityType == EntityType.STORE) {
        return tableFields;
    }

    if (props.entityType == EntityType.OPTION_SET) {
        return tableFields;
    }

    return tableFieldsWithThumbnail;
};

const cancelHandler = () => {
    if (mode.value == 'add') {
        changeToListMode();
    } else if (mode.value == 'list') {
        emit('cancel');
    }
};

const forceCloseHandler = () => {
    emit('cancel');
};

const confirmHandler = async () => {
    if (mode.value == 'add') {
        changeToListMode();
    } else if (mode.value == 'list') {
        emit('permissions-saved', props.member!);
    }
};

const removeEntityAccess = (entity: EntityData) => {
    const index = entitiesAdded.value.map((t) => t.id).indexOf(entity.id);
    entitiesAdded.value.splice(index, 1);
    removeTeamMemberEntities(props.member!.user.Id, [entity.id], props.entityType);
};

const addEntityAccess = (entity: EntityData) => {
    entitiesAdded.value.push(entity);
    addTeamMemberEntities(props.member!.user.Id, [entity.id], props.entityType);
};

const changeCheckedEntityAccess = (type: string) => {
    checkedEntities.value = checkedEntities.value.map((entity) => {
        if (entity.isChecked) {
            if (type === 'add') addEntityAccess(entity);
            if (type === 'remove') removeEntityAccess(entity);
        }
        return { ...entity, isChecked: false };
    });
    checkAllPage(false);
};

const checkAllPage = (value: boolean) => {
    entitiesToDisplay.value = entitiesToDisplay.value.map((entity) => ({ ...entity, isChecked: value }));
    for (const entityToStore of entitiesToDisplay.value) {
        storeCheck(value, entityToStore);
    }
};

const storeCheck = (value: boolean, entity: EntityData) => {
    const isStored = checkedEntities.value.some((entityStored) => entityStored.id === entity.id);
    if (isStored) {
        checkedEntities.value = checkedEntities.value.map((entityStored) => {
            if (entityStored.id !== entity.id) return entityStored;
            return {
                ...entityStored,
                isChecked: value
            };
        });
    } else {
        checkedEntities.value.push({ ...entity, isChecked: value });
    }
};

const init = async () => {
    if (props.entityType == EntityType.PRODUCT) {
        showStoresDropdown.value = true;
        const storesResult = await getTeamMemberStores(props.member!.user.Id, 0, 100);
        availableStores.value = storesResult.Stores;
        if (availableStores.value && availableStores.value.length > 0) {
            selectedStore.value = availableStores.value[0];
        }
    }

    changeToListMode();

    if (props.entityType === EntityType.TEMPLATE && props.member?.user?.Id) {
        try {
            const initAccessRestriction = await getTeamMemberAccessRestriction(props.member.user.Id, props.entityType);
            accessRestriction.value = initAccessRestriction;
        } catch (error) {
            console.error(`Failed to load ${props.entityType} access restriction:`, error);
            accessRestriction.value = AccessRestriction.LISTED;
        }
    }
};

const changeToAddMode = () => {
    mode.value = 'add';
    searchString.value = '';
    currentPage.value = 1;

    fetchEntities();
};

const changeToListMode = () => {
    mode.value = 'list';
    searchString.value = '';
    currentPage.value = 1;

    fetchEntitiesWithAccess();
};

const fetchEntitiesWithAccess = async () => {
    switch (props.entityType) {
        case EntityType.TEMPLATE:
            return await fetchTemplatesWithAccess();
        case EntityType.LIBRARY:
            return await fetchLibrariesWithAccess();
        case EntityType.STORE:
            return await fetchStoresWithAccess();
        case EntityType.PRODUCT:
            return await fetchProductsWithAccess();
        case EntityType.OPTION_SET:
            return await fetchOptionSetsWithAccess();
    }
};

const fetchEntities = async () => {
    switch (props.entityType) {
        case EntityType.TEMPLATE:
            return await fetchTemplateProducts();
        case EntityType.LIBRARY:
            return await fetchLibraries();
        case EntityType.STORE:
            return await fetchStores();
        case EntityType.PRODUCT:
            return await fetchProducts();
        case EntityType.OPTION_SET:
            return await fetchOptionSets();
    }
};

const fetchTemplatesWithAccess = async () => {
    if (mode.value == 'add') {
        return;
    }

    loading.value = true;
    try {
        const from = (currentPage.value - 1) * itemsPerPage;
        const templatesResult = await getTeamMemberTemplates(
            props.member!.user.Id,
            from,
            itemsPerPage,
            searchString.value
        );
        const mappedEntities = templatesResult.TemplateProducts.map((t) => ({
            id: t.id,
            name: t.name,
            thumbnail: buildCloudFrontUrl(t.thumbnailPath)
        }));
        entitiesAdded.value = mappedEntities;
        entitiesToDisplay.value = mappedEntities;
        totalCount.value = templatesResult.Count;
    } catch (error) {
        console.error('Failed to fetch templates with access', error);
    }
    loading.value = false;
};

const fetchLibrariesWithAccess = async () => {
    if (mode.value == 'add') {
        return;
    }

    loading.value = true;
    try {
        const from = (currentPage.value - 1) * itemsPerPage;
        const to = from + itemsPerPage;
        const librariesResult = await getTeamMemberLibraries(props.member!.user.Id, from, to, searchString.value);
        const mappedEntities = librariesResult.Libraries.map((l) => ({
            id: l.LibraryId.toString(),
            name: l.Name,
            thumbnail: l.ThumbnailPath ? buildCloudFrontUrl(l.ThumbnailPath) : ''
        }));
        entitiesAdded.value = mappedEntities;
        entitiesToDisplay.value = mappedEntities;
        totalCount.value = librariesResult.Count;
    } catch (error) {
        console.error('Failed to fetch libraries with access', error);
    }
    loading.value = false;
};

const fetchStoresWithAccess = async () => {
    if (mode.value == 'add') {
        return;
    }

    loading.value = true;
    try {
        const from = (currentPage.value - 1) * itemsPerPage;
        const to = from + itemsPerPage;
        const storesResult = await getTeamMemberStores(props.member!.user.Id, from, to, searchString.value);
        const mappedEntities = storesResult.Stores.map((s) => ({
            id: s.Id.toString(),
            name: s.Url,
            thumbnail: ''
        }));
        entitiesAdded.value = mappedEntities;
        entitiesToDisplay.value = mappedEntities;
        totalCount.value = storesResult.Count;
    } catch (error) {
        console.error('Failed to fetch stores with access', error);
    }
    loading.value = false;
};

const fetchProductsWithAccess = async () => {
    if (mode.value == 'add' || !selectedStore.value?.Url) {
        return;
    }

    loading.value = true;
    try {
        const from = (currentPage.value - 1) * itemsPerPage;
        const to = from + itemsPerPage;
        const productsResult = await getTeamMemberProducts(
            props.member!.user.Id,
            from,
            to,
            selectedStore.value?.Url,
            searchString.value
        );
        const mappedEntities = productsResult.Products.map((p) => ({
            id: `${p.productId}`,
            name: p.name,
            thumbnail: p.imageSrc
        }));
        entitiesAdded.value = mappedEntities;
        entitiesToDisplay.value = mappedEntities;
        totalCount.value = productsResult.Count;
    } catch (error) {
        console.error('Failed to fetch products with access', error);
    }
    loading.value = false;
};

const fetchOptionSetsWithAccess = async () => {
    if (mode.value == 'add') {
        return;
    }

    loading.value = true;
    try {
        const from = (currentPage.value - 1) * itemsPerPage;
        const to = from + itemsPerPage;
        const setsResult = await getTeamMemberOptionSets(props.member!.user.Id, from, to, searchString.value);
        const mappedEntities = setsResult.OptionSets.map((s) => ({
            id: s.id,
            name: s.title,
            thumbnail: ''
        }));
        entitiesAdded.value = mappedEntities;
        entitiesToDisplay.value = mappedEntities;
        totalCount.value = setsResult.Count;
    } catch (error) {
        console.error('Failed to fetch option sets with access', error);
    }
    loading.value = false;
};

const fetchTemplateProducts = async () => {
    if (mode.value == 'list') {
        return;
    }

    loading.value = true;
    try {
        const from = (currentPage.value - 1) * itemsPerPage;
        let templatesResult = await getTemplates(from, itemsPerPage, searchString.value);
        const mappedEntities = templatesResult.templateProducts.map((t) => ({
            id: t.id,
            name: t.name,
            thumbnail: buildCloudFrontUrl(t.thumbnailPath),
            isChecked: checkedEntities.value?.some((entity) => entity.id === t.id && entity.isChecked) || false
        }));
        searchEntities.value = mappedEntities;
        entitiesToDisplay.value = mappedEntities;
        totalCount.value = templatesResult.totalCount;
    } catch (error) {
        console.error('Failed to fetch templates', error);
    }
    loading.value = false;
};

const fetchLibraries = async () => {
    if (mode.value == 'list') {
        return;
    }

    loading.value = true;
    try {
        const librariesResult = await getLibraries([0, 1, 2, 3], currentPage.value, itemsPerPage, searchString.value);
        const mappedEntities = librariesResult.Libraries.map((l) => ({
            id: l.LibraryId.toString(),
            name: l.Name,
            thumbnail: l.ThumbnailPath ? buildCloudFrontUrl(l.ThumbnailPath) : '',
            isChecked:
                checkedEntities.value?.some((entity) => entity.id === l.LibraryId.toString() && entity.isChecked) ||
                false
        }));
        searchEntities.value = mappedEntities;
        entitiesToDisplay.value = mappedEntities;
        totalCount.value = librariesResult.Count;
    } catch (error) {
        console.error('Failed to fetch libraries', error);
    }
    loading.value = false;
};

const fetchStores = async () => {
    if (mode.value == 'list') {
        return;
    }

    loading.value = true;
    try {
        const from = (currentPage.value - 1) * itemsPerPage;
        const to = from + itemsPerPage;
        const stores = await getStores(from, to, searchString.value);
        const mappedEntities = stores.Stores.map((l) => ({
            id: l.Id.toString(),
            name: l.Url,
            thumbnail: '',
            isChecked:
                checkedEntities.value?.some((entity) => entity.id === l.Id.toString() && entity.isChecked) || false
        }));
        searchEntities.value = mappedEntities;
        entitiesToDisplay.value = mappedEntities;
        totalCount.value = stores.Count;
    } catch (error) {
        console.error('Failed to fetch stores', error);
    }
    loading.value = false;
};

const fetchProducts = async () => {
    if (mode.value == 'list' || !selectedStore.value?.Url) {
        return;
    }

    loading.value = true;
    try {
        const from = (currentPage.value - 1) * itemsPerPage;
        const products = await getProductsWithCount(
            selectedStore.value?.Url,
            searchString.value,
            from,
            itemsPerPage,
            true,
            0
        );
        const mappedEntities = products.products.map((p) => ({
            id: `${p.productId}`,
            name: p.name,
            thumbnail: p.imageSrc,
            isChecked:
                checkedEntities.value?.some((entity) => entity.id === `${p.productId}` && entity.isChecked) || false
        }));
        searchEntities.value = mappedEntities;
        entitiesToDisplay.value = mappedEntities;
        totalCount.value = products.total;
    } catch (error) {
        console.error('Failed to fetch products', error);
    }
    loading.value = false;
};

const fetchOptionSets = async () => {
    if (mode.value == 'list') {
        return;
    }

    loading.value = true;
    try {
        const from = (currentPage.value - 1) * itemsPerPage;
        const optionSets = await getOptionSetsWithCount(searchString.value, from, itemsPerPage);
        const mappedEntities = optionSets.sets.map((o) => ({
            id: o.id,
            name: o.title,
            thumbnail: '',
            isChecked: checkedEntities.value?.some((entity) => entity.id === o.id && entity.isChecked) || false
        }));
        searchEntities.value = mappedEntities;
        entitiesToDisplay.value = mappedEntities;
        totalCount.value = optionSets.totalCount;
    } catch (error) {
        console.error('Failed to fetch option sets', error);
    }
    loading.value = false;
};

const updateAccessRestriction = async (accessRestrictionParam: AccessRestriction, entityType: EntityType) => {
    if (!props.member?.user?.Id) {
        console.error('updateAccessRestriction: Member user ID is not available');
        return;
    }

    const previousMode = accessRestriction.value;

    try {
        loading.value = true;
        accessRestriction.value = accessRestrictionParam;
        await updateTeamMemberAccessRestriction(props.member.user.Id, accessRestrictionParam, entityType);

        // Update the member object and emit to parent for persistence
        if (props.member) {
            props.member.accessRestriction = accessRestrictionParam;
            // Create a copy of the updated member to emit
            const updatedMember = { ...props.member };
            emit('member-updated', updatedMember);
        }
    } catch (error) {
        // Revert on error
        accessRestriction.value = previousMode;
        console.error(
            `Failed to update for entityType ${entityType} accesssRestriction to ${accessRestrictionParam} for member: ${props.member.user.Id}`,
            error
        );

        const errorMessage = error instanceof Error ? error.message : error?.toString();
        alert(
            `Failed to update for entityType ${entityType} accessRestriction to ${accessRestrictionParam}, error: ${errorMessage}`
        );
    } finally {
        loading.value = false;
    }
};

const onStoresDropdownClick = (store: Store) => {
    if (selectedStore.value !== store) {
        selectedStore.value = store;
        if (mode.value == 'list') {
            fetchEntitiesWithAccess();
        } else {
            fetchEntities();
        }
    }
    storesDropdown.value?.show();
};

const getAccessModeText = (mode: AccessRestriction) => {
    switch (mode) {
        case AccessRestriction.LISTED:
            return 'Listed';
        case AccessRestriction.FULL_ACCESS:
            return 'Full Access';
        case AccessRestriction.ALL_READ_ONLY_PLUS_LISTED:
            return 'All Read-Only + Full Access to Listed';
        default:
            return 'Listed';
    }
};

onMounted(init);

watch([searchString, currentPage], () => {
    fetchEntitiesWithAccess();
    fetchEntities();
});
</script>

<style lang="scss" scoped>
.search-input-gray {
    background: var(--Gray-scale-white, #fefefe) !important;
    color: var(--Gray-scale-gray, #bfbfbf) !important;
    text-overflow: ellipsis;
}

.search-input-gray::placeholder {
    color: var(--Gray-scale-gray, #bfbfbf) !important;
}

:deep() .table-header {
    color: #747474;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
}

:deep() .checkCell {
    padding: 0.75rem 0 0.75rem 13px !important;
    & > div {
        height: 40px;
    }
}

:deep() .check-head-container {
    & > :first-child {
        margin-left: 13px;
    }
    & > span:nth-child(2) {
        color: #747474;
        font-size: 14px;
        font-style: normal;
        font-weight: 600;
        line-height: normal;
    }
    & > span:nth-child(3) {
        color: var(--Primary-primary, #7258e0);
        font-size: 11px;
        margin: 0 0.7rem;
    }
}

.entityContentContainer {
    position: relative;
}

.entityContent.disabled {
    pointer-events: none; /* deshabilita interacción */
    opacity: 0.6; /* opcional: efecto visual */
}

.overlay {
    margin: -8px;
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    filter: grayscale(100%);
    backdrop-filter: blur(1px);
    border-radius: 8px;
    background: rgba(0, 0, 0, 0.3);
    z-index: 10;
    color: white;
    font-size: 1.2rem;
}
</style>
